/**
 * Shared password policy — single source of truth for every form and server route.
 *
 * Aligns with Keycloak realm password policy (minimum 12 chars, all four character
 * classes required). Only the characters below count toward the special-character
 * requirement, plus non-leading/non-trailing spaces.
 */

export const PASSWORD_MIN_LENGTH = 12

export const PASSWORD_SYMBOLS = '^$*.[]{}()?-"!@#%&/\\,><\':;|_~`+='

/** @deprecated Use PASSWORD_SYMBOLS */
export const COGNITO_SYMBOLS = PASSWORD_SYMBOLS

export const hasPasswordSymbol = (password: string): boolean =>
  [...password].some((char, index) =>
    PASSWORD_SYMBOLS.includes(char)
    || (char === ' ' && index > 0 && index < password.length - 1)
  )

/** @deprecated Use hasPasswordSymbol */
export const hasCognitoSymbol = hasPasswordSymbol

export const hasNoEdgeSpaces = (password: string): boolean =>
  password === password.trim()

/** True when the password contains special characters that do not count toward policy (e.g. ç, ã, é). */
export const hasUncountedSpecialChar = (password: string): boolean =>
  [...password].some(char =>
    !/[A-Za-z0-9]/.test(char) && char !== ' ' && !PASSWORD_SYMBOLS.includes(char)
  )

export const passwordChecks = {
  minLength: (password: string) => password.length >= PASSWORD_MIN_LENGTH,
  uppercase: (password: string) => /[A-Z]/.test(password),
  lowercase: (password: string) => /[a-z]/.test(password),
  number: (password: string) => /[0-9]/.test(password),
  symbol: hasPasswordSymbol
}

export const isValidPassword = (password: string): boolean =>
  Object.values(passwordChecks).every(check => check(password)) && hasNoEdgeSpaces(password)
